import type { ClientPersonReference } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu } from 'react-native-paper';

import {
  BlockIcon,
  FeedbackIcon,
  Inventory2Icon,
  MoreVerticalIcon
} from '@/assets';
import { useArchiveChat } from '@/features/api/hooks/communication-chat-events/useArchiveChat';
import { useUnarchiveChat } from '@/features/api/hooks/communication-chat-events/useUnarchiveChat';
import { useBlockPerson } from '@/features/api/hooks/participant-person-block-events/useBlockPerson';
import { useUnblockPerson } from '@/features/api/hooks/participant-person-block-events/useUnblockPerson';
import { useFlag } from '@/features/flag/providers/useFlag';
import { FlagEntityTypeEnum } from '@/features/flag/types/flagEntityType';
import {
  getPersonAvatarPicture,
  getPersonAvatarTextFallback
} from '@/features/media/utils/person';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import { getPersonName } from '@/shared/utils/person';

type Props = {
  chatPartner?: ClientPersonReference;
  chatId?: string;
  archived?: boolean;
};

export const ChatNavigationBar: FC<Props> = ({
  chatPartner,
  chatId,
  archived
}) => {
  const { t } = useTranslation(LocalizationNameSpace.CHATS, {
    keyPrefix: 'screens.chat'
  });
  const { flag } = useFlag();
  const { mutate: blockPerson } = useBlockPerson();
  const { mutate: unblockPerson } = useUnblockPerson();
  const archiveChat = useArchiveChat();
  const unarchiveChat = useUnarchiveChat();
  const navigation = useAppNavigation();

  if (!chatPartner) {
    return <NavigationBar />;
  }

  const deleted = chatPartner.deleted ?? false;
  const blocked = chatPartner?.filterStatus === 'BLOCKED';
  const personName = deleted ? t('accountDeleted') : getPersonName(chatPartner);

  return (
    <NavigationBar
      picture={getPersonAvatarPicture(chatPartner)}
      pictureFallback={{ text: getPersonAvatarTextFallback(chatPartner) }}
      title={personName}
      titleVariant="titleMedium"
      buttons={[
        {
          name: t('menu'),
          asMenu: true,
          icon: MoreVerticalIcon,
          menuItems: (onItemPress) => (
            <>
              {chatId && (
                <Menu.Item
                  leadingIcon={({ color, size }) => (
                    <FeedbackIcon fill={color} width={size} height={size} />
                  )}
                  title={t('reportViolation')}
                  onPress={() => {
                    onItemPress();
                    flag(FlagEntityTypeEnum.Chat, chatId);
                  }}
                />
              )}
              {chatPartner && (
                <Menu.Item
                  leadingIcon={({ color, size }) => (
                    <BlockIcon fill={color} width={size} height={size} />
                  )}
                  title={t(blocked ? 'unblockUser' : 'blockUser')}
                  onPress={() => {
                    onItemPress();
                    if (blocked) {
                      unblockPerson(chatPartner.id);
                    } else {
                      blockPerson(chatPartner.id);
                    }
                  }}
                />
              )}
              {chatId && (
                <Menu.Item
                  leadingIcon={({ color, size }) => (
                    <Inventory2Icon fill={color} width={size} height={size} />
                  )}
                  title={t(archived ? 'unarchive' : 'archive')}
                  onPress={() => {
                    onItemPress();

                    if (archived) {
                      unarchiveChat.mutate(chatId);
                    } else {
                      archiveChat.mutate(chatId, {
                        onSuccess: () => {
                          if (navigation.canGoBack()) navigation.goBack();
                        }
                      });
                    }
                  }}
                />
              )}
            </>
          )
        }
      ]}
    />
  );
};
